import { homeRow } from './board';
import { buildCards } from './cards';
import { RULES_VERSION, validateConfig } from './config';
import { epitaphSlots, initLedger } from './ledger/state';
import { dealStartingHands, setupRound } from './rules';
import { Pcg32, STREAM_AI, STREAM_MATCH, hex64, seedToBigInt } from './rng';
import type {
  Card,
  MatchConfig,
  MatchEvent,
  MatchState,
  Pawn,
  PawnSpec,
  PlayerState,
  ReservePawn,
  Side,
} from './types';

const defaultPawnSpecs = (side: Side): PawnSpec[] =>
  [...'ABCDEF'].map((f, file) => ({ id: `${side}-${f}`, file }));

function makePawn(side: Side, spec: PawnSpec): Pawn {
  const startSquare = { x: spec.file, y: homeRow(side) };
  return {
    id: spec.id,
    side,
    startSquare,
    square: startSquare,
    status: 'onBoard',
    crowned: false,
    inscription: null,
    ...(spec.name !== undefined ? { name: spec.name } : {}),
    ...(spec.standIn ? { standIn: true } : {}),
    ...(spec.former ? { former: true } : {}),
    ...(spec.carvings && spec.carvings.length
      ? { carvings: spec.carvings.map((c) => ({ ...c })) }
      : {}),
  };
}

const newPlayer = (side: Side, deck: string[], reserves: string[]): PlayerState => ({
  side,
  deck,
  hand: [],
  spent: [],
  removed: [],
  epitaphs: [],
  epitaphPlays: 0,
  passed: false,
  passKind: null,
  roundScore: 0,
  totalScore: 0,
  roundWins: 0,
  pendingCrownBonuses: [],
  reserves,
  reservesPawnedThisRound: [],
  collateralUsed: [],
});

/**
 * Match setup (§4.1) followed by round 1 setup (§5.1). Returns the initial state and its events.
 * The match stream is consumed in order: side assignment, South's shuffle, North's shuffle,
 * round-1 starter.
 */
export function startMatch(
  config: MatchConfig,
  seed: string | number,
): { state: MatchState; events: MatchEvent[] } {
  const problems = validateConfig(config);
  if (problems.length > 0) throw new Error(`Invalid match config: ${problems.join('; ')}`);
  const seed64 = seedToBigInt(seed);
  const matchRng = Pcg32.seeded(seed64, STREAM_MATCH);
  const aiSeed = hex64(Pcg32.seeded(seed64, STREAM_AI).nextU64());

  const [p1, p2] = config.seating.players;
  const flip = config.seating.assignment === 'random' && matchRng.nextBelow(2) === 1;
  const seats = flip ? { S: p2, N: p1 } : { S: p1, N: p2 };

  const cards: Record<string, Card> = {};
  const decks = {} as Record<Side, string[]>;
  for (const side of ['S', 'N'] as const) {
    const list = buildCards(config, side);
    for (const c of list) cards[c.id] = c;
    decks[side] = matchRng.shuffle(list.map((c) => c.id));
  }

  const pawns: Record<string, Pawn> = {};
  const reservePawns: Record<string, ReservePawn> = {};
  const reserveIds = {} as Record<Side, string[]>;
  for (const side of ['S', 'N'] as const) {
    for (const spec of config.pawns?.[side] ?? defaultPawnSpecs(side))
      pawns[spec.id] = makePawn(side, spec);
    reserveIds[side] = [];
    for (const r of config.reserves?.[side] ?? []) {
      reservePawns[r.id] = {
        id: r.id,
        side,
        ...(r.name !== undefined ? { name: r.name } : {}),
        ...(r.standIn ? { standIn: true } : {}),
      };
      reserveIds[side].push(r.id);
    }
  }

  const events: MatchEvent[] = [
    {
      type: 'MatchStarted',
      rulesVersion: RULES_VERSION,
      mode: config.mode,
      preset: config.preset,
      seats,
    },
  ];
  const state: MatchState = {
    rulesVersion: RULES_VERSION,
    config,
    seats,
    rng: { match: matchRng.toState(), ai: { seed: aiSeed } },
    seq: 0,
    cards,
    pawns,
    reservePawns,
    players: { S: newPlayer('S', decks.S, reserveIds.S), N: newPlayer('N', decks.N, reserveIds.N) },
    pile: [],
    round: 1,
    roundStarter: 'S',
    turn: 0,
    active: 'S',
    decider: 'S',
    phase: 'TURN_START',
    chain: null,
    pendingInscription: null,
    pendingArrivals: null,
    pendingEpitaphChoice: null,
    nextEpitaphSeq: 1,
    rounds: [],
    result: null,
  };
  // Long Night tables carry a ledger, with a luck stream of its own (§16.2's streams untouched).
  const ledger = initLedger(config, seed64);
  if (ledger) {
    state.ledger = ledger;
    for (const side of ['S', 'N'] as const)
      state.players[side].epitaphs = (config.ledger?.startEpitaphs?.[side] ?? [])
        .slice(0, epitaphSlots(state, side))
        .map((e) => ({ ...e, owner: side }));
  }
  dealStartingHands(state, events);
  setupRound(state, 1, events);
  return { state, events };
}

/** createMatch(config, seed) → state (§16.1). */
export const createMatch = (config: MatchConfig, seed: string | number): MatchState =>
  startMatch(config, seed).state;
